import { ReadonlyJSONObject, ReadonlyJSONValue, ReadTransaction, WriteTransaction } from "@workcanvas/reflect";
import {
  authenticatedDelete,
  authenticatedPut,
  canvasElementPrefix,
  createElementId,
  getUnixTimestampUTC,
  sanityMutationId,
  validateSchema,
} from "../util/utils";
import { AttachedConnector, CanvasElement, Point } from "./schemas/canvas-element";
import { sanitizeUrl } from "../util/sanitize-url";
import { applyPatches, enablePatches, Patch } from "immer";
import consts from "shared/consts";
import {
  shapeSchema,
  stickyNoteSchema,
  textBlockSchema,
  drawingSchema,
  connectorSchema,
  fileSchema,
  taskCardSchema,
  frameSchema,
  commentThreadSchema,
  integrationItemSchema,
  tableElementSchema,
} from "shared/datamodel/schemas";
import { cardStackSchema } from "shared/datamodel/schemas/card-stack";
import { liveIntegrationSchema } from "shared/datamodel/schemas/live-integration";
import { mindmapNodeSchema } from "shared/datamodel/schemas/mindmap";
import { mindmapOrgChartNodeSchema } from "shared/datamodel/schemas/mindmap-org-chart";
import { orgChartSchema } from "shared/datamodel/schemas/org-chart";
import { timelineSchema } from "shared/datamodel/schemas/timeline";

enablePatches();

// For performance reasons, we do not use this parsing method anymore.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function parseElementByType(type: string, value: ReadonlyJSONValue | undefined) {
  switch (type) {
    case consts.CANVAS_ELEMENTS.SHAPE: {
      return validateSchema(shapeSchema, value);
    }
    case consts.CANVAS_ELEMENTS.STICKY_NOTE: {
      return validateSchema(stickyNoteSchema, value);
    }
    case consts.CANVAS_ELEMENTS.TEXT_BLOCK: {
      return validateSchema(textBlockSchema, value);
    }
    case consts.CANVAS_ELEMENTS.DRAWING: {
      return validateSchema(drawingSchema, value);
    }
    case consts.CANVAS_ELEMENTS.CONNECTOR: {
      return validateSchema(connectorSchema, value);
    }
    case consts.CANVAS_ELEMENTS.FILE: {
      return validateSchema(fileSchema, value);
    }
    case consts.CANVAS_ELEMENTS.TASK_CARD: {
      return validateSchema(taskCardSchema, value);
    }
    case consts.CANVAS_ELEMENTS.FRAME: {
      return validateSchema(frameSchema, value);
    }
    case consts.CANVAS_ELEMENTS.COMMENT: {
      return validateSchema(commentThreadSchema, value);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP: {
      return validateSchema(mindmapNodeSchema, value);
    }
    case consts.CANVAS_ELEMENTS.MINDMAP_ORG_CHART: {
      return validateSchema(mindmapOrgChartNodeSchema, value);
    }
    case consts.CANVAS_ELEMENTS.INTEGRATION: {
      return validateSchema(integrationItemSchema, value);
    }
    case consts.CANVAS_ELEMENTS.ORG_CHART: {
      return validateSchema(orgChartSchema, value);
    }
    case consts.CANVAS_ELEMENTS.CARD_STACK: {
      return validateSchema(cardStackSchema, value);
    }
    case consts.CANVAS_ELEMENTS.LIVE_INTEGRATION: {
      return validateSchema(liveIntegrationSchema, value);
    }
    case consts.CANVAS_ELEMENTS.TIMELINE: {
      return validateSchema(timelineSchema, value);
    }
    case consts.CANVAS_ELEMENTS.TABLE: {
      return validateSchema(tableElementSchema, value);
    }
    default: {
      console.error("failed to parse element - invalid type " + type);
      return null;
    }
  }
}

export async function getElement(tx: ReadTransaction, id: string, type?: string): Promise<CanvasElement | null> {
  if (id.startsWith("tableCell-")) {
    const jv = await tx.get(key(id));
    if (typeof jv == "object" && jv !== null) return { ...jv } as CanvasElement;
    else return null;
  }
  const idOnly = type ? id : id.split("-")[1];
  const typeOnly = type || id.split("-")[0];
  const elementID = type ? `${typeOnly}-${idOnly}` : `${typeOnly}-${idOnly}`;
  const jv = await tx.get(key(elementID));

  if (!jv) {
    console.log(`Specified shape ${elementID} not found.`);
    return null;
  }
  if (typeof jv == "object" && jv !== null) return { ...jv } as CanvasElement;
  else return null;
}

export function putElement(
  tx: WriteTransaction,
  { id, element }: { id: string; element: CanvasElement }
): Promise<void> {
  const next = { ...(element as ReadonlyJSONObject), lastModifiedTimestamp: getUnixTimestampUTC() };
  return authenticatedPut(tx, key(id), next);
}

export async function putElements(
  tx: WriteTransaction,
  { ids, elements }: { ids: string[]; elements: { element: any }[] }
): Promise<void> {
  const lastModifiedTimestamp = getUnixTimestampUTC();
  await Promise.all(
    ids.map((id, index) =>
      authenticatedPut(tx, key(id), { ...(elements[index].element as ReadonlyJSONObject), lastModifiedTimestamp })
    )
  );
}

export async function sanityCheck(
  tx: WriteTransaction,
  { originTimestamp }: { originTimestamp: number }
): Promise<void> {
  return tx.set(`${sanityMutationId}-${tx.clientID}`, { originTimestamp });
}

export async function putAnys(tx: WriteTransaction, items: ReadonlyArray<[string, any]>): Promise<void> {
  const lastModifiedTimestamp = getUnixTimestampUTC();
  if (tx.location === "client" || !tx.auth?.isReadOnly) {
    await Promise.all(
      items.map(([key, value]) => tx.set(key, { ...value, lastModifiedTimestamp } as ReadonlyJSONObject))
    );
  }
}

export async function removeMany(tx: WriteTransaction, { ids }: { ids: string[] }) {
  for (const id of ids) {
    await authenticatedDelete(tx, key(id));
  }
}

export async function addAttachedConnector(
  tx: WriteTransaction,
  {
    id,
    connectorId,
    attachedConnector,
  }: {
    id: string;
    connectorId: string;
    attachedConnector: AttachedConnector;
  }
): Promise<void> {
  const element = await getElement(tx, id);
  if (element && element.attachedConnectors) {
    const newElement = { ...element, attachedConnectors: { ...element.attachedConnectors } };
    newElement.attachedConnectors[connectorId] = attachedConnector;
    await putElement(tx, { id, element: newElement });
  }
}

export async function removeAttachedConnector(
  tx: WriteTransaction,
  {
    id,
    connectorId,
    anchorIndex,
  }: {
    id: string;
    connectorId: string;
    anchorIndex: number;
  }
): Promise<void> {
  const element = await getElement(tx, id);
  if (element && element.attachedConnectors && element.attachedConnectors.hasOwnProperty(connectorId)) {
    if (element.attachedConnectors[connectorId]!.index === anchorIndex) {
      delete element.attachedConnectors[connectorId];
    }
    await putElement(tx, { id, element: element });
  }
}

export async function copyAttachedConnector(
  tx: WriteTransaction,
  {
    copyFromId,
    elementType,
    elementCopy,
    connectorId,
    copiedConnectorId,
  }: {
    copyFromId: string;
    elementType: string;
    elementCopy: { id: string; element: CanvasElement };
    connectorId: string;
    copiedConnectorId: string;
  }
): Promise<void> {
  const element = await getElement(tx, copyFromId);
  if (element && element.attachedConnectors) {
    const anchoredShape = element.attachedConnectors[connectorId]!;
    const copiedAnchoredShape = {
      lineId: copiedConnectorId,
      offsetX: anchoredShape.offsetX,
      anchorMode: anchoredShape.anchorMode,
      index: anchoredShape.index,
      position: anchoredShape.position,
    } as AttachedConnector;
    elementCopy.element.attachedConnectors![copiedConnectorId] = copiedAnchoredShape;
    await putElement(tx, {
      id: `${elementType}-${elementCopy.id}`,
      element: elementCopy.element,
    });
  }
}

export async function putAttachedConnectorDragState(
  tx: WriteTransaction,
  {
    id,
    connectorId,
  }: {
    id: string;
    connectorId: string;
  }
): Promise<void> {
  const element = await getElement(tx, id);
  if (element && element.attachedConnectors && element.attachedConnectors[connectorId]) {
    await putElement(tx, { id, element: element });
  }
}

export async function changeElementProperties(
  tx: WriteTransaction,
  { id, properties }: { id: string; properties: { key: string; value: any }[] }
): Promise<void> {
  const element: any = await getElement(tx, id);
  if (element) {
    let didModify = false;
    for (const { key, value } of properties) {
      if (typeof value === "undefined") {
        didModify = true;
        delete element[key];
      } else if (element[key] !== value) {
        didModify = true;
        element[key] = key === "link" ? sanitizeUrl(value) : value;
      }
    }
    if (didModify) {
      await putElement(tx, { id, element: element as CanvasElement });
    }
  }
}

export async function deleteElement(tx: WriteTransaction, id: string): Promise<void> {
  await authenticatedDelete(tx, key(id));
}

export async function moveElement(
  tx: WriteTransaction,
  { id, dx, dy }: { id: string; dx: number; dy: number }
): Promise<void> {
  const element = await getElement(tx, id);
  if (element) {
    element.x = dx;
    element.y = dy;
    await putElement(tx, { id, element: element });
  }
}

export type ElementDragInfo = {
  id: string;
  position: Point;
};

export async function moveElements(
  tx: WriteTransaction,
  { multiDragInfo }: { multiDragInfo: ElementDragInfo[] }
): Promise<void> {
  for (const dragInfo of multiDragInfo) {
    const element = await getElement(tx, dragInfo.id);
    if (element) {
      element.x = dragInfo.position.x;
      element.y = dragInfo.position.y;
      await putElement(tx, { id: dragInfo.id, element: element });
    }
  }
}

export type ElementMoveInfo = {
  ids: string[];
  dx: number;
  dy: number;
};

export async function moveElementsByDelta(tx: WriteTransaction, moveInfo: ElementMoveInfo): Promise<void> {
  await Promise.all(
    moveInfo.ids.map((id) =>
      getElement(tx, id).then((element) => {
        if (element) {
          element.x += moveInfo.dx;
          element.y += moveInfo.dy;
          return putElement(tx, { id: id, element: element });
        }
      })
    )
  );
}

export type ElementDrawProps = {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  rotate?: number;
  hidden?: boolean;
  zIndexLastChangeTime?: number;
  groupId?: string;
  groupHistory?: string[];
  frameId?: string;
  sortIndex?: number;
  containerId?: string | null;
  itemsDates?: {
    [k: string]: number;
  };
};

export async function attachToFrame(
  tx: WriteTransaction,
  { ids, frameId }: { ids: string[]; frameId: string | null }
): Promise<void> {
  return Promise.all(
    ids.map(async (id) => {
      const element = await getElement(tx, id);
      if (!element) {
        return;
      }
      const { frameId: oldFrameId, ...rest } = element;
      if (frameId) {
        (rest as any).frameId = frameId;
      }
      await putElement(tx, { id, element: rest });
    })
  ).then();
}

export async function changeElements(
  tx: WriteTransaction,
  { info }: { info: [string, ElementDrawProps][] }
): Promise<void> {
  await Promise.all(
    info.map(async ([id, props]) => {
      const element = await getElement(tx, id);
      if (element) {
        await putElement(tx, { id, element: { ...element, ...props } });
      }
    })
  );
}

export async function positionElement(
  tx: WriteTransaction,
  { id, x, y }: { id: string; x: number; y: number }
): Promise<void> {
  const element = await getElement(tx, id);
  if (element) {
    element.x = x;
    element.y = y;
    await putElement(tx, { id, element: element });
  }
}

function key(id: string): string {
  return `${canvasElementPrefix}${id}`;
}

export type NewElementData = {
  id: string;
  type: string;
  element: any; //CanvasElement;
};

export function copyElement(
  id: string,
  element: CanvasElement,
  offsetX: number = 50,
  offsetY: number = 50
): NewElementData {
  const [elementType] = id.split("-");
  return {
    id: createElementId(),
    type: elementType,
    element: {
      ...element,
      x: element.x + offsetX,
      y: element.y + offsetY,
    },
  };
}

export interface PatchInfo {
  id: string;
  patch: Patch[];
}

export async function patchCanvasEl(
  tx: WriteTransaction,
  args: { changes: PatchInfo[]; fetchedElements?: CanvasElement[] }
) {
  // the single case is more common, do it first
  if (args.changes.length == 1) {
    const elKey = key(args.changes[0].id);
    const element = await tx.get(elKey);
    return element
      ? authenticatedPut(tx, elKey, applyPatches(element as any, args.changes[0].patch))
      : Promise.resolve();
  }
  // TODO: use fetchedElements for better performance
  async function getPatchAndWrite({ id, patch }: PatchInfo) {
    const theKey = key(id);
    const existing = await tx.get(theKey);
    if (!existing) {
      return;
    }
    const newVersion = applyPatches(existing as any, patch);
    return authenticatedPut(tx, theKey, newVersion);
  }
  await Promise.all(args.changes.map(getPatchAndWrite));
  return Promise.resolve();
}

// TODO
// this function is very similar to patchCanvasEl, they can be united into one I think
function isArrayOfPatches(changes: PatchInfo | ReadonlyArray<PatchInfo>): changes is ReadonlyArray<PatchInfo> {
  return Array.isArray(changes);
}

export async function patchAnything(tx: WriteTransaction, changes: PatchInfo | ReadonlyArray<PatchInfo>) {
  if (tx.location === "client" || !tx.auth?.isReadOnly) {
    const now = getUnixTimestampUTC();
    if (isArrayOfPatches(changes)) {
      await Promise.all(
        changes.map(({ id, patch }) =>
          tx
            .get(id)
            .then((value) => applyPatches((value as any) || {}, patch))
            .then((newValue) => tx.set(id, { ...newValue, lastModifiedTimestamp: now }))
        )
      );
    } else {
      const newValue = applyPatches(((await tx.get(changes.id)) as any) || {}, changes.patch);
      await tx.set(changes.id, { ...newValue, lastModifiedTimestamp: now });
    }
  }
}

export type ElementTextProperties = {
  font?: string;
  fontSize?: number;
  align?: "center" | "left" | "right";
  textColor?: string;
  fontProps?: number;
};
